import { useState } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { httpBatchLink } from '@trpc/client';
import {
  Layout,
  SignIn,
  Academics,
  UnitWorkloadDetailsView,
  SchoolUnit,
  UnitTeachingDeliveryView,
  UnitTeachingRelatedDutiesView,
  AdminLayout,
  UserAccessManagement,
  RemoveUnits,
  ModifySchoolParameters,
  ErrorHandler,
  Report,
  RemoveAcademics,
  Discipline,
  DataLoad,
  HdrAllocations,
} from '@/components';
import { AWMSLanding } from '@/components/AWMSLanding';
import { trpc } from '@/data-loaders';
import { config } from './env-exports';
import { UpdateStaffDetails } from './components/Views/Admin/UpdateStaffDetails';

let accessToken: string | undefined = undefined;

//TODO: remove route allocate-workload as its not used anymore
const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    errorElement: (
      <ErrorHandler error={{ message: 'Invalid URL' }} resetErrorBoundary={() => console.log('Error boundary reset')} />
    ),

    children: [
      {
        index: true,
        element: <AWMSLanding />,
      },
      {
        path: 'staffs',
        children: [
          {
            index: true,
            element: <Academics />,
          },
          {
            path: 'allocate-workload',
            children: [
              {
                index: true,
                element: <Academics />,
              },
            ],
          },
        ],
      },
      {
        path: 'unit-workloads',
        children: [
          {
            index: true,
            element: <SchoolUnit />,
          },
          {
            path: ':unitWorkloadId',
            children: [
              {
                index: true,
                element: <UnitWorkloadDetailsView />,
              },
              {
                path: 'teaching-delivery',
                element: <UnitTeachingDeliveryView />,
              },
              {
                path: 'teaching-related-duties',
                element: <UnitTeachingRelatedDutiesView />,
              },
            ],
          },
        ],
      },
      {
        path: 'admin',
        element: <AdminLayout />,
        children: [
          {
            index: true,
            element: <UserAccessManagement />,
          },
          {
            path: 'user-access-management',
            element: <UserAccessManagement />,
          },
          {
            path: 'remove-academics',
            element: <RemoveAcademics />,
          },
          {
            path: 'remove-units',
            element: <RemoveUnits />,
          },
          {
            path: 'school-parameters',
            element: <ModifySchoolParameters />,
          },
          {
            path: 'data-load',
            element: <DataLoad />,
          },
          {
            path: 'disciplines',
            element: <Discipline />,
          },
          {
            path: 'update-staff-details',
            element: <UpdateStaffDetails />,
          },
          {
            path: 'hdr-allocations',
            element: <HdrAllocations />,
          },
        ],
      },
      {
        path: 'reports',
        element: <Report />,
      },
      {
        path: '/signin',
        element: <SignIn />,
      },
      {
        path: '/signout',
        element: <Navigate to="/" />,
      },
    ],
  },
]);

export default function App() {
  const auth = useAuth();

  if (!auth.isAuthenticated && !auth.isLoading) {
    localStorage.removeItem('context-and-conditions-of-use');
    if (window.location.pathname && window.location.pathname !== '/signin') {
      localStorage.setItem('signin-restore-pathname', `${window.location.pathname}${window.location.search}`);
      localStorage.setItem('signin-restore-referrer', document.referrer);
    }

    auth.signinRedirect();
  }

  const apiUri = config.apiBaseUri ?? window.location.origin;
  const [queryClient] = useState(() => new QueryClient());
  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        httpBatchLink({
          url: `${apiUri}/api/trpc`,
          // Split into multiple requests before the batched GET URL trips the WAF (prod only).
          // AWSManagedRulesCommonRuleSet blocks query strings over 2048 bytes
          // (SizeRestrictions_QUERYSTRING) and URI paths over 1024 (SizeRestrictions_URIPATH),
          // returning 403 at the edge. maxURLLength is measured against the whole URL, so this
          // bounds the query string (the limit that caused INC0592322) but does not by itself
          // bound the path. With today's procedure names and input shapes the path peaks at
          // ~945 bytes before this cap splits the batch; a new procedure with a long name and a
          // single short-named field could change that. Use 1080 if an unconditional path
          // guarantee is ever needed. INC0592322
          maxURLLength: 2000,
          async headers() {
            return {
              authorization: `Bearer ${accessToken}`,
            };
          },
          async fetch(url, options) {
            const res = await fetch(url, options);
            // 🛑 Catch raw API Gateway error "User is not authorized to access this resource with an explicit deny" (unsure why) before tRPC processes it
            if (res.status === 403) {
              // Changed from 401 to 403 to match the server's response
              // This is to handle the case where the API Gateway returns a 403 Forbidden error
              const text = await res.clone().text(); //https://stackoverflow.com/questions/53511974/javascript-fetch-failed-to-execute-json-on-response-body-stream-is-locked

              if (text.includes('explicit deny')) {
                console.warn('API Gateway Authorisation Failure:', text);
                throw new Error('Session expired');
              }
            }
            return res;
          },
        }),
      ],
    }),
  );
  if (auth.isAuthenticated) {
    accessToken = auth.user?.access_token;
  }

  return (
    <>
      {auth.isAuthenticated ? (
        <trpc.Provider client={trpcClient} queryClient={queryClient}>
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
          </QueryClientProvider>
        </trpc.Provider>
      ) : null}
    </>
  );
}
